import { useState, useEffect } from 'react'
import { Helmet } from 'react-helmet-async'
import { motion } from 'framer-motion'
import { 
  DocumentTextIcon,
  EyeIcon, 
  ComputerDesktopIcon,
  ClockIcon,
  MagnifyingGlassIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline'

interface LogEntry {
  id: string
  timestamp: string
  level: 'info' | 'warning' | 'error' | 'debug'
  category: string
  message: string
  details?: any
  ip_address?: string
  user_agent?: string
  session_id?: string
}

const LogsPage = () => {
  const [logs, setLogs] = useState<LogEntry[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [levelFilter, setLevelFilter] = useState('all')
  const [categoryFilter, setCategoryFilter] = useState('all')
  const [autoRefresh, setAutoRefresh] = useState(true)

  // Generate mock log entries for demo
  const generateMockLogs = () => {
    const levels: ('info' | 'warning' | 'error' | 'debug')[] = ['info', 'warning', 'error', 'debug']
    const categories = ['auth', 'tracking', 'api', 'security', 'system']
    const messages = [
      'User login attempt',
      'Page view tracked',
      'API request processed',
      'Suspicious activity detected',
      'System health check',
      'Database connection established',
      'Rate limit exceeded',
      'Session expired',
      'File download initiated',
      'Authentication successful'
    ]

    const mockLogs: LogEntry[] = []
    
    for (let i = 0; i < 50; i++) {
      const timestamp = new Date(Date.now() - Math.random() * 24 * 60 * 60 * 1000).toISOString()
      const level = levels[Math.floor(Math.random() * levels.length)]
      const category = categories[Math.floor(Math.random() * categories.length)]
      const message = messages[Math.floor(Math.random() * messages.length)]
      
      mockLogs.push({
        id: `log-${Date.now()}-${i}`,
        timestamp,
        level,
        category,
        message,
        ip_address: `192.168.1.${Math.floor(Math.random() * 255)}`,
        user_agent: 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36',
        session_id: `session-${Math.random().toString(36).substr(2, 9)}`
      })
    }
    
    return mockLogs.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
  }

  useEffect(() => {
    const loadLogs = async () => {
      setLoading(true)
      try {
        // For demo purposes, generate mock logs
        await new Promise(resolve => setTimeout(resolve, 500)) // Simulate API delay
        setLogs(generateMockLogs())
      } catch (err) {
        setError('Failed to load logs')
      } finally {
        setLoading(false)
      }
    }
    
    loadLogs()
    
    if (autoRefresh) {
      const interval = setInterval(() => {
        // Add new log entries periodically
        setLogs(prev => {
          const newLogs = [...prev]
          const newEntry: LogEntry = {
            id: `log-${Date.now()}`,
            timestamp: new Date().toISOString(),
            level: Math.random() > 0.8 ? 'warning' : 'info',
            category: 'tracking',
            message: 'New page view tracked',
            ip_address: `192.168.1.${Math.floor(Math.random() * 255)}`,
            session_id: `session-${Math.random().toString(36).substr(2, 9)}`
          }
          newLogs.unshift(newEntry)
          return newLogs.slice(0, 100) // Keep only last 100 logs
        })
      }, 5000)
      
      return () => clearInterval(interval)
    }
  }, [autoRefresh])

  const getLevelColor = (level: string) => {
    switch (level) {
      case 'info': return 'bg-blue-100 text-blue-800'
      case 'warning': return 'bg-yellow-100 text-yellow-800'
      case 'error': return 'bg-red-100 text-red-800'
      case 'debug': return 'bg-gray-100 text-gray-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const getLevelIcon = (level: string) => {
    switch (level) {
      case 'info': return <InformationCircleIcon className="h-4 w-4" />
      case 'warning': return <ExclamationTriangleIcon className="h-4 w-4" />
      case 'error': return <ExclamationTriangleIcon className="h-4 w-4" />
      case 'debug': return <DocumentTextIcon className="h-4 w-4" />
      default: return <ClockIcon className="h-4 w-4" />
    }
  }

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'tracking': return <EyeIcon className="h-4 w-4" />
      case 'auth': return <ComputerDesktopIcon className="h-4 w-4" />
      case 'api': return <DocumentTextIcon className="h-4 w-4" />
      case 'security': return <ExclamationTriangleIcon className="h-4 w-4" />
      default: return <ClockIcon className="h-4 w-4" />
    }
  }

  const filteredLogs = logs.filter(log => {
    const matchesSearch = searchTerm === '' || 
      log.message.toLowerCase().includes(searchTerm.toLowerCase()) ||
      log.category.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (log.ip_address || '').toLowerCase().includes(searchTerm.toLowerCase())
    
    const matchesLevel = levelFilter === 'all' || log.level === levelFilter
    const matchesCategory = categoryFilter === 'all' || log.category === categoryFilter
    
    return matchesSearch && matchesLevel && matchesCategory
  })

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent-800 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading logs...</p>
        </div>
      </div>
    )
  }

  return (
    <>
      <Helmet>
        <title>System Logs - CyberScrape Training Platform</title>
        <meta name="description" content="System logs and activity monitoring for instructor oversight" />
      </Helmet>

      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">System Logs</h1>
            <p className="text-sm text-gray-600">
              Monitor system activities and security events
            </p>
          </div>
          
          <div className="flex items-center space-x-4">
            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
                className="rounded border-gray-300 text-accent-600 focus:ring-accent-500"
              />
              <span className="text-sm text-gray-600">Auto-refresh</span>
            </label>
            
            <div className="flex items-center space-x-2">
              <div className={`w-2 h-2 rounded-full ${autoRefresh ? 'bg-green-500 animate-pulse' : 'bg-gray-400'}`}></div>
              <span className="text-sm text-gray-600">
                {autoRefresh ? 'Live' : 'Paused'}
              </span>
            </div>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-center">
              <ExclamationTriangleIcon className="h-5 w-5 text-red-500 mr-2" />
              <p className="text-red-700">{error}</p>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Search */}
            <div className="flex-1">
              <div className="relative">
                <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search logs by message, category, or IP..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-500 focus:border-accent-500"
                />
              </div>
            </div>

            {/* Level Filter */}
            <div className="sm:w-40">
              <select
                value={levelFilter}
                onChange={(e) => setLevelFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-500 focus:border-accent-500"
              >
                <option value="all">All Levels</option>
                <option value="info">Info</option>
                <option value="warning">Warning</option>
                <option value="error">Error</option>
                <option value="debug">Debug</option>
              </select>
            </div>

            {/* Category Filter */}
            <div className="sm:w-40">
              <select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-500 focus:border-accent-500"
              >
                <option value="all">All Categories</option>
                <option value="auth">Auth</option>
                <option value="tracking">Tracking</option>
                <option value="api">API</option>
                <option value="security">Security</option>
                <option value="system">System</option>
              </select>
            </div>
          </div>
        </div>

        {/* Logs List */}
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">
              Log Entries ({filteredLogs.length})
            </h2>
          </div>
          
          <div className="divide-y divide-gray-200 max-h-96 overflow-y-auto">
            {filteredLogs.map((log, index) => (
              <motion.div
                key={log.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.02 }}
                className="p-4 hover:bg-gray-50 transition-colors duration-200"
              >
                <div className="flex items-start space-x-3">
                  {/* Level Icon */}
                  <div className={`p-2 rounded-full ${getLevelColor(log.level)}`}>
                    {getLevelIcon(log.level)}
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    {/* Header */}
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center space-x-2">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${getLevelColor(log.level)}`}>
                          {log.level.toUpperCase()}
                        </span>
                        
                        <div className="flex items-center space-x-1 text-gray-500">
                          {getCategoryIcon(log.category)}
                          <span className="text-sm font-medium">{log.category}</span>
                        </div>
                      </div>
                      
                      <span className="text-xs text-gray-500">
                        {new Date(log.timestamp).toLocaleString()}
                      </span>
                    </div>
                    
                    {/* Message */}
                    <p className="text-sm text-gray-900 mb-2">{log.message}</p>
                    
                    {/* Details */}
                    {(log.ip_address || log.session_id) && (
                      <div className="flex items-center space-x-4 text-xs text-gray-500">
                        {log.ip_address && (
                          <span>IP: {log.ip_address}</span>
                        )}
                        {log.session_id && (
                          <span>Session: {log.session_id.slice(-8)}</span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </motion.div>
            ))}
            
            {filteredLogs.length === 0 && (
              <div className="text-center py-12">
                <DocumentTextIcon className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">
                  {searchTerm || levelFilter !== 'all' || categoryFilter !== 'all' ? 
                    'No logs match your filters' : 
                    'No log entries found'
                  }
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  )
}

export default LogsPage
